import { useState } from "react";
import { FileProcessingAnswers, FileProcessingConfig } from "./fileProcessing";

type FileProcessingQuestionsProps = {
  file: File;
  config: FileProcessingConfig;
  onComplete: (answers: FileProcessingAnswers) => void;
};

function FileProcessingQuestions({
  config,
  onComplete,
}: FileProcessingQuestionsProps) {
  const [answers, setAnswers] = useState<FileProcessingAnswers>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const currentQuestion = config.questions[currentQuestionIndex];

  const handleOptionSelect = (key: string, value: boolean) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));

    // Automatically move to next question after selection
    if (currentQuestionIndex < config.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleSubmit = () => {
    if (config.questions.every((q) => answers[q.key] !== undefined)) {
      onComplete(answers);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center max-w-2xl mx-auto p-8 bg-white">
      <div className="w-full space-y-6">
        {/* Progress indicator */}
        <div className="flex justify-between items-center mb-8">
          <div className="text-sm text-gray-500">
            Question {currentQuestionIndex + 1} of {config.questions.length}
          </div>
          <div className="h-2 flex-1 mx-4 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{
                width: `${
                  ((currentQuestionIndex + 1) / config.questions.length) * 100
                }%`,
              }}
            />
          </div>
        </div>

        {/* Current question */}
        <div key={currentQuestion.key} className="space-y-6">
          <h3 className="text-2xl font-semibold text-center text-gray-800">
            {currentQuestion.question}
          </h3>

          <div className="grid grid-cols-1 gap-4 mt-8">
            {currentQuestion.options.map((option) => (
              <button
                key={String(option.value)}
                className={`w-full p-4 text-lg font-medium rounded-lg transition-all duration-200
                  ${
                    answers[currentQuestion.key] === option.value
                      ? option.value === true
                        ? "bg-green-500 text-white shadow-lg scale-105"
                        : option.value === false
                        ? "bg-red-500 text-white shadow-lg scale-105"
                        : "bg-blue-500 text-white shadow-lg scale-105"
                      : "bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-500 hover:shadow-md"
                  }
                  ${
                    option.value === true
                      ? "hover:bg-green-100 hover:border-green-500"
                      : option.value === false
                      ? "hover:bg-red-100 hover:border-red-500"
                      : "hover:bg-blue-100 hover:border-blue-500"
                  }
                `}
                onClick={() =>
                  handleOptionSelect(currentQuestion.key, option.value)
                }
              >
                {option.label}
              </button>
            ))}
          </div>

          {currentQuestion.note && (
            <div className="flex items-center justify-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-amber-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="block text-md text-amber-600">
                {currentQuestion.note}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Only show submit button on last question */}
      {currentQuestionIndex === config.questions.length - 1 && (
        <button
          className="mt-8 bg-green-500 text-white px-8 py-3 rounded-lg font-medium shadow-lg
            hover:bg-green-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSubmit}
          disabled={
            !config.questions.every((q) => answers[q.key] !== undefined)
          }
        >
          Complete
        </button>
      )}
    </div>
  );
}

export default FileProcessingQuestions;
